import React, { useState, useEffect, useCallback } from "react";
import debounce from "lodash/debounce";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import { useQuery } from "@apollo/client/react";
import useLabels from "../hooks/useLabels";
import InboundTable from "./project/inbound/InboundTable";
import { GET_SHIPMENTS_QUERY } from "../constants/graphql/queries";
import InboundTableSkeleton from "./project/inbound/InboundTableSkeleton";

export default function InboundSummaryModal({ asset, onClose }) {
  const l = useLabels();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState(asset.itemId);
  const [debouncedSearch, setDebouncedSearch] = useState(asset.itemId);
  const variables = { itemId: debouncedSearch };
  const { data, loading } = useQuery(GET_SHIPMENTS_QUERY, { variables });
  const shipments = data?.shipments?.collection || [];
  const metadata = data?.shipments?.metadata || { totalCount: 0, totalPages: 0, currentPage: 1 };

  const debouncedSetSearch = useCallback(
    debounce(value => {
      setDebouncedSearch(value);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSetSearch(searchInput);
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [searchInput, debouncedSetSearch]);

  const handleToggle = shipment => {
    const params = new URLSearchParams(location.search);
    if (params.get("receiving")) {
      navigate({ pathname: location.pathname, search: "" });
    } else {
      params.set("step", "shipment");
      params.set("receiving", "true");
      params.set("editShipment", "true");
      params.set("shipmentId", shipment.shipmentId);
      params.set("id", shipment.id);
      navigate({ pathname: location.pathname, search: params.toString() });
    }
  };

  const handleSearchChange = event => {
    setSearchInput(event.target.value);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography fontWeight={500} fontSize={22} color="textPrimary" align="center">
          {l.identifiers.receiving_report_number}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box mb={2}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search"
            size="small"
            value={searchInput}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {loading ? (
          <InboundTableSkeleton />
        ) : shipments.length > 0 ? (
          <InboundTable
            list={shipments}
            variables={variables}
            page={metadata.currentPage}
            totalPages={metadata.totalPages}
            totalCount={metadata.totalCount}
            onEdit={handleToggle}
            displayCustomerNotified={false}
          />
        ) : (
          <Box p={3} textAlign="center">
            <Typography variant="body1">No inbound items found.</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
